import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Plus, MessageCircle, LogOut, User, Crown, Shield, Coins } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/NotificationBell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 glass border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 via-fuchsia-500 to-pink-500 flex items-center justify-center shadow-lg shadow-fuchsia-500/30 group-hover:scale-105 transition-transform">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <span className="font-display font-extrabold text-xl tracking-tight text-white">
            Joy<span className="text-fuchsia-400">Buzz</span>
          </span>
        </Link>

        <div className="flex items-center gap-2 sm:gap-3">
          {user && (
            <>
              <Link
                to="/coins"
                data-testid="nav-coins"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-amber-500/10 border border-amber-400/30 text-amber-300 hover:bg-amber-500/20 transition-all"
              >
                <Coins className="w-3.5 h-3.5" /> {(user.coins ?? 0).toLocaleString("de-DE")}
              </Link>
              {user.is_vip ? (
                <span
                  data-testid="nav-vip-badge"
                  className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-amber-500/20 to-fuchsia-500/20 border border-amber-400/40 text-amber-300"
                >
                  <Crown className="w-3.5 h-3.5" /> {user.is_owner ? "OWNER" : "VIP"}
                </span>
              ) : (
                <Link
                  to="/vip"
                  data-testid="nav-go-vip"
                  className="flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full text-xs font-bold bg-gradient-to-r from-amber-500/15 to-fuchsia-500/15 border border-amber-400/30 text-amber-300 hover:from-amber-500/25 hover:to-fuchsia-500/25 transition-all"
                >
                  <Crown className="w-3.5 h-3.5" /> Go VIP
                </Link>
              )}
              <Link
                to="/my-chats"
                data-testid="nav-my-chats"
                className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-all"
              >
                <MessageCircle className="w-4 h-4" /> My Chats
              </Link>
              <Link
                to="/create"
                data-testid="nav-create-character"
                className="flex items-center gap-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white font-semibold text-sm rounded-full px-4 sm:px-5 py-2.5 shadow-lg shadow-violet-500/20 active:scale-95 transition-all"
              >
                <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Create</span>
              </Link>
              <NotificationBell />
            </>
          )}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-user-menu"
                  className="w-10 h-10 rounded-full bg-gradient-to-br from-fuchsia-500 to-indigo-500 flex items-center justify-center text-white font-bold text-sm border border-white/10 active:scale-95 transition-all"
                >
                  {user.name?.[0]?.toUpperCase() || "U"}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="glass border-white/10 text-slate-200"
              >
                <div className="px-3 py-2 text-sm">
                  <p className="font-semibold text-white">{user.name}</p>
                  <p className="text-xs text-slate-400">{user.email}</p>
                </div>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem
                  data-testid="nav-profile"
                  onClick={() => navigate("/profile")}
                  className="cursor-pointer focus:bg-white/10"
                >
                  <User className="w-4 h-4 mr-2" /> Profil
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="nav-my-chats-menu"
                  onClick={() => navigate("/my-chats")}
                  className="cursor-pointer focus:bg-white/10"
                >
                  <MessageCircle className="w-4 h-4 mr-2" /> My Chats
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="nav-subscription"
                  onClick={() => navigate("/subscription")}
                  className="cursor-pointer focus:bg-white/10"
                >
                  <Crown className="w-4 h-4 mr-2" /> Mein Abo
                </DropdownMenuItem>
                {user.is_owner && (
                  <DropdownMenuItem
                    data-testid="nav-owner-dashboard"
                    onClick={() => navigate("/owner")}
                    className="cursor-pointer focus:bg-white/10 text-amber-300"
                  >
                    <Shield className="w-4 h-4 mr-2" /> Owner Dashboard
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  data-testid="nav-logout"
                  onClick={() => {
                    logout();
                    navigate("/");
                  }}
                  className="cursor-pointer focus:bg-white/10 text-rose-400"
                >
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login"
              className="flex items-center gap-2 bg-white/10 hover:bg-white/15 text-white border border-white/10 rounded-full px-5 py-2 text-sm font-medium active:scale-95 transition-all"
            >
              <User className="w-4 h-4" /> Sign in
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};
